import { Flex, Skeleton, Card, CardBody, SimpleGrid } from '@chakra-ui/react';
import React from 'react';

import { date as commonDate } from '@finley-technologies/common';

import { useGetCreditFacilityMetricsQuery } from '../../../../../types/dashboard-core-gqlTypes.generated.d';
import { CapitalizationServicerFeeSection } from 'packages/invoices/ui/servicerFee/CapitalizationServicerFeeSection';
import { CreditFacility } from '~/contexts/CreditFacilitiesProvider';
import { PermissionGuard } from '~/core/PermissionGuard';
import { Typography } from '~/packages/common-frontend/components/Typography';
import {
  dollarNumberWithCommas,
  percentageToPercentageLabel,
} from '~/packages/common-frontend/utils';
import { useGetCreditFacilityBorrowerEntityNameQuery } from '~/packages/credit-facility/requests/requests.gql.generated';
import { useGetSummarizedLoansQuery } from '~/packages/loans/requests/loans.gql.generated';
import { LoansTable } from '~/packages/loans/ui/LoansTable';

// Constants
const INITIAL_TANSTACK_TABLE_STATE_LOANS = {
  pagination: {
    pageSize: 10,
    pageIndex: 0,
  },
};

// Fragments
type SummarySectionItemProps = {
  label: React.ReactNode;
  value: React.ReactNode;
};
const SummarySectionItem = ({ label, value }: SummarySectionItemProps) => (
  <Flex direction="column" gap="0.5rem" flexGrow={1}>
    <Typography variant="detail-muted">{label}</Typography>
    <Typography variant="base" fontWeight="medium">
      {value}
    </Typography>
  </Flex>
);

type SummarySectionProps = { creditFacility: CreditFacility };
const SummarySection = ({ creditFacility }: SummarySectionProps) => {
  const facilityMetricsQuery = useGetCreditFacilityMetricsQuery({
    variables: {
      creditFacilityId: creditFacility.id,
      postingDate: commonDate.currentDateStr(),
    },
  });
  if (facilityMetricsQuery.error) throw facilityMetricsQuery.error;

  const borrowerEntityNameQuery = useGetCreditFacilityBorrowerEntityNameQuery({
    variables: {
      creditFacilityId: creditFacility.id,
    },
  });
  if (borrowerEntityNameQuery.error) throw borrowerEntityNameQuery.error;

  return (
    <Flex direction="column" width="100%" gap="1rem">
      <Typography variant="section-title" trim>
        Summary
      </Typography>
      {(() => {
        if (
          !facilityMetricsQuery.data?.metrics ||
          facilityMetricsQuery.loading ||
          borrowerEntityNameQuery.loading ||
          !borrowerEntityNameQuery.data
        ) {
          return <Skeleton h="12.5rem" w="100%" />;
        }

        const {
          outstandingPrincipalBalance,
          utilization,
          commitmentAmount,
          externalFacilityId,
        } = facilityMetricsQuery.data.metrics;

        return (
          <Card variant="outline" size="lg" flexGrow={1}>
            <CardBody display="flex">
              <SimpleGrid
                columns={{ sm: 2, md: 3, lg: 3, xl: 3 }}
                flexGrow={1}
                gap="1.5rem"
              >
                <SummarySectionItem
                  label="Borrower"
                  value={
                    borrowerEntityNameQuery.data.creditFacility
                      .borrowerEntityName
                  }
                />
                {/* @jendevelops TODO pipe in borrower organization into cached cf */}
                {/* <SummarySectionItem label="Industry" value="PLACEHOLDER" /> */}
                {/* <SummarySectionItem
                  label="Accrued Interest & Fees"
                  value={dollarNumberWithCommas(
                    // TODO: @markhaghani - replace with totalAccruedInterestAndFees
                    totalAccruedInterest + totalAccruedServiceFees,
                  )}
                /> */}
                <SummarySectionItem
                  label="Commitment"
                  value={dollarNumberWithCommas(commitmentAmount)}
                />
                <SummarySectionItem
                  label="Outstanding Balance"
                  value={dollarNumberWithCommas(outstandingPrincipalBalance)}
                />
                <SummarySectionItem
                  label="Utilization"
                  value={percentageToPercentageLabel(utilization * 100, 2)}
                />
                <SummarySectionItem
                  label="External Facility ID"
                  value={externalFacilityId ?? '-'}
                />
              </SimpleGrid>
            </CardBody>
          </Card>
        );
      })()}
    </Flex>
  );
};

type LoansSectionProps = { creditFacilityId: string };
const LoansSection = ({ creditFacilityId }: LoansSectionProps) => {
  const summarizedLoansQuery = useGetSummarizedLoansQuery({
    fetchPolicy: 'network-only',
    variables: {
      filterParams: {
        creditFacilityIds: [creditFacilityId],
        isFinal: true,
      },
    },
  });
  if (summarizedLoansQuery.error) throw summarizedLoansQuery.error;

  return (
    <Flex direction="column" width="100%">
      <Typography variant="section-title" trim>
        Loans
      </Typography>
      <LoansTable
        initialTanstackTableState={INITIAL_TANSTACK_TABLE_STATE_LOANS}
        loading={summarizedLoansQuery.loading}
        loans={summarizedLoansQuery.data?.loans ?? []}
      />
    </Flex>
  );
};

const AccruedServicerFeeSection = ({
  creditFacilityId,
}: {
  creditFacilityId: string;
}) => {
  return (
    <PermissionGuard
      permissions={(context) =>
        context.flags.isSlsAgentLender ? 'allow' : 'deny'
      }
    >
      <Typography variant="section-title">Accrued Servicer Fees</Typography>
      <CapitalizationServicerFeeSection
        creditFacilityId={creditFacilityId}
        includeFacilityNameColumn={false}
      />
    </PermissionGuard>
  );
};

// Exports
type PortfolioSummaryProps = { creditFacility: CreditFacility };
export const PortfolioSummary = ({ creditFacility }: PortfolioSummaryProps) => {
  return (
    <Flex direction="column" gap="2.5rem">
      <SummarySection creditFacility={creditFacility} />
      <LoansSection creditFacilityId={creditFacility.id} />
      <AccruedServicerFeeSection creditFacilityId={creditFacility.id} />
    </Flex>
  );
};
